"use client";

import { cn } from "@/lib/utils";
import Link, { LinkProps } from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import * as React from "react";

export interface NavLinkProps extends Omit<LinkProps, "className"> {
  /**
   * The href to navigate to
   */
  href: string;
  /**
   * Class name(s) to apply to the link.
   * Can be a string or a function that receives `{ isActive }` and returns a string.
   */
  className?: string | ((props: { isActive: boolean }) => string);
  /**
   * Class name to apply when the link is active.
   * This is applied in addition to the base className.
   */
  activeClassName?: string;
  /**
   * If true, the link will only be active when the pathname exactly matches the href.
   * Default: false
   */
  exact?: boolean;
  /**
   * If true, the link will only be active when the pathname exactly matches the href
   * or when the pathname starts with href followed by a slash.
   * This is useful for nested routes.
   * Default: false
   */
  end?: boolean;
  /**
   * Children to render inside the link
   */
  isActive?: boolean;
  children?: React.ReactNode;
}

function parseHref(href: string): {
  pathname: string;
  searchParams: URLSearchParams;
} {
  const [pathnamePart, search = ""] = href.split("?");
  return {
    pathname: pathnamePart || "/",
    searchParams: new URLSearchParams(search),
  };
}

function searchParamsMatch(
  hrefParams: URLSearchParams,
  currentParams: URLSearchParams,
): boolean {
  if ([...hrefParams.keys()].length === 0) {
    return true;
  }

  for (const [key, value] of hrefParams.entries()) {
    if (currentParams.get(key) !== value) {
      return false;
    }
  }

  return true;
}

export const NavLink = React.forwardRef<HTMLAnchorElement, NavLinkProps>(
  (
    {
      href,
      className,
      activeClassName,
      exact = false,
      end = false,
      isActive = false,
      children,
      ...props
    },
    ref,
  ) => {
    const pathname = usePathname();
    const currentSearchParams = useSearchParams();
    const { pathname: hrefPathname, searchParams: hrefSearchParams } =
      React.useMemo(() => parseHref(href), [href]);

    const _isActive = React.useMemo(() => {
      if (isActive) {
        return true;
      }

      const queryMatches = searchParamsMatch(
        hrefSearchParams,
        currentSearchParams,
      );

      if (!queryMatches) {
        return false;
      }

      if (exact || hrefSearchParams.toString().length > 0) {
        return pathname === hrefPathname;
      }

      if (end) {
        return pathname === hrefPathname || pathname === `${hrefPathname}/`;
      }

      return (
        pathname === hrefPathname || pathname.startsWith(`${hrefPathname}/`)
      );
    }, [
      pathname,
      hrefPathname,
      hrefSearchParams,
      currentSearchParams,
      exact,
      end,
      isActive,
    ]);

    // Compute the final className
    const computedClassName = React.useMemo(() => {
      if (typeof className === "function") {
        return className({ isActive: _isActive });
      }

      if (_isActive && activeClassName) {
        return cn(className, activeClassName);
      }

      return className;
    }, [className, activeClassName, _isActive]);

    return (
      <Link ref={ref} href={href} className={computedClassName} {...props}>
        {children}
      </Link>
    );
  },
);

NavLink.displayName = "NavLink";
